import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useHistory } from 'react-router-dom';
import { FaCheck, FaRocket, FaGem, FaArrowLeft, FaShieldAlt, FaHeadset, FaServer, FaCar, FaCalculator, FaCheckCircle, FaMobileAlt, FaTimes, FaPlayCircle } from 'react-icons/fa';
import { ehPlanoGratuito, estaEmPeriodoDeTeste, obterTipoPlano, expiraEm } from '../../services/seguranca';
import { parseDataBRParaDate } from '../../services/utilitarios';
import { useAppSelector } from '../../store/hooks';
import { toast } from 'react-toastify';
import { iniciarSessaoFunil, rastrearEventoFunil } from '../../services/rastreamentoFunil';

// --- Animações ---
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
  100% { transform: translateY(0px); }
`;

// --- Estrutura ---
const Page = styled.div`
  min-height: 100vh;
  background-color: #f8fafc;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-x: hidden;
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #1F467E 0%, #162f56 100%);
  padding: 0.75rem 1.5rem 1.5rem;
  position: relative;

  @media (max-width: 900px) {
    padding: 0.75rem 1rem 1.25rem;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 20% 150%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  position: relative;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 1.5rem 1.5rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 900px) {
    padding: 1rem 1rem 0;
  }
`;

const TopBar = styled.div`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  padding: 0.6rem 1.2rem;
  border-radius: 30px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateX(-3px);
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  flex: 1;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 800;
  color: white;
  margin-bottom: 0.15rem;
  letter-spacing: -0.02em;
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);

  @media (max-width: 900px) {
    font-size: 1.25rem;
    margin-bottom: 0.1rem;
  }
`;

const Subtitle = styled.p`
  color: #e2e8f0;
  font-size: 0.9rem;
  line-height: 1.3;
  opacity: 0.9;
  margin-bottom: 0;

  @media (max-width: 900px) {
    font-size: 0.82rem;
    line-height: 1.2;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 1.5rem;
  width: 100%;
  justify-content: center;
  margin-bottom: 2rem;

  @media (max-width: 900px) {
    gap: 1.25rem;
    margin-bottom: 1.5rem;
  }
`;

// Alteração aqui: Adicionado mobileOrder e media query
const Card = styled.div<{ highlight?: boolean; isCurrent?: boolean; mobileOrder?: number }>`
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.08);
  border: 1px solid ${({ highlight }) => highlight ? '#bfdbfe' : '#e2e8f0'};
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: visible;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${fadeInUp} 0.6s ease-out backwards;
  height: 100%;

  /* Animação escalonada */
  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }

  ${({ highlight, isCurrent }) => (highlight || isCurrent) && css`
    border: 2px solid #1F467E;
    transform: scale(1.02);
    box-shadow: 0 20px 50px -12px rgba(31, 70, 126, 0.25);
    z-index: 2;
  `}

  &:hover {
    transform: translateY(-8px) ${({ highlight }) => highlight ? 'scale(1.02)' : ''};
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
  }

  /* --- CORREÇÃO MOBILE --- */
  @media (max-width: 900px) {
    order: ${({ mobileOrder }) => mobileOrder || 0};
    /* Remove o scale no mobile para não cortar nas laterais se a tela for muito pequena */
    ${({ highlight, isCurrent }) => (highlight || isCurrent) && css`
        transform: scale(1);
    `}
  }
`;

const Badge = styled.div<{ color?: 'green' | 'blue' | 'yellow' }>`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ color }) => color === 'green' ? '#10b981' : color === 'blue' ? '#2563eb' : '#f59e0b'};
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 6px 16px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
`;

const CardBody = styled.div`
  padding: 1.5rem 1.75rem;
  flex: 1;
  display: flex;
  flex-direction: column;

  @media (max-width: 900px) {
    padding: 1.25rem 1.25rem;
  }
`;

const PlanHeader = styled.div`
  text-align: center;
`;

const PlanIcon = styled.div`
  width: 48px;
  height: 48px;
  background: #eff6ff;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 0.6rem;
  color: #1F467E;
  font-size: 1.5rem;
  transition: transform 0.3s ease;

  ${Card}:hover & {
    animation: ${float} 2s ease-in-out infinite;
    background: #1F467E;
    color: white;
  }
`;

const PlanTitle = styled.h3`
  font-size: 1.35rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.25rem;
`;

const PlanPrice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  .prefix {
    font-size: 0.9rem;
    color: #64748b;
    font-weight: 500;
  }

  .price-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    color: #1F467E;

    span { font-size: 1.1rem; font-weight: 600; margin-top: 4px; }
    strong { font-size: 2.5rem; font-weight: 800; line-height: 1; }
    small { color: #64748b; font-weight: 500; font-size: 0.9rem; align-self: flex-end; margin-bottom: 4px; }
  }

  .description {
    margin-top: 0.25rem;
    font-size: 0.85rem;
    color: #64748b;
    margin-bottom: 0px;
    line-height: 1.4;

    strong {
      color: #1e293b;
      font-weight: 700;
    }
  }
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
  margin: 0.75rem 0 1rem;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const FeatureItem = styled.li<{ strong?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 0.9rem;
  color: ${({ strong }) => (strong ? '#0f172a' : '#475569')};
  font-weight: ${({ strong }) => (strong ? '600' : '400')};
  line-height: 1.3;

  svg {
    color: #10b981;
    font-size: 1.1rem;
    flex-shrink: 0;
    margin-top: 1px;
    filter: drop-shadow(0 2px 2px rgba(16, 185, 129, 0.2));
  }
`;

const MissingFeature = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 0.85rem;
  color: #94a3b8;
  font-weight: 400;
  line-height: 1.3;
  text-decoration: line-through;

  svg {
    color: #cbd5e1;
    font-size: 1rem;
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const AppHighlight = styled.div`
  margin-top: 1.25rem;
  padding: 1rem;
  background: linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%);
  border: 1.5px solid #bfdbfe;
  border-radius: 12px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #2563eb, #10b981);
    border-radius: 4px 0 0 4px;
  }
`;

const AppHighlightHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 700;
  color: #1e40af;
  margin-bottom: 0.4rem;

  svg {
    font-size: 1.2rem;
    color: #2563eb;
  }
`;

const FeaturesRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;

  @media (max-width: 600px) {
    flex-direction: column;
  }

  & > ${FeaturesList} {
    flex: 1;
    min-width: 0;
  }

  & > ${AppHighlight} {
    flex: 1;
    min-width: 0;
    margin-top: 0;
  }
`;

const SupportBadge = styled.div`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #fef3c7 0%, #fff7ed 100%);
  border: 1.5px solid #f59e0b;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #d97706;
    font-size: 1.4rem;
    flex-shrink: 0;
  }

  div {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  strong {
    font-size: 0.82rem;
    color: #92400e;
  }

  span {
    font-size: 0.78rem;
    color: #a16207;
  }
`;

const AppFeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.82rem;
    font-weight: 500;
    color: #1e293b;

    svg {
      color: #10b981;
      font-size: 0.85rem;
      flex-shrink: 0;
    }
  }
`;

const VideoLink = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #2563eb;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0;
  margin-top: 0.6rem;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: #1d4ed8;
  }
`;

const VideoModalContent = styled.div`
  width: 90vw;
  max-width: 800px;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
  text-align: center;
`;

const ModalDescription = styled.p`
  font-size: 0.9rem;
  color: #475569;
  margin: 0 0 1rem 0;
  text-align: center;
`;

const ModalFeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.88rem;
    font-weight: 500;
    color: #1e293b;

    svg {
      color: #10b981;
      font-size: 1rem;
      flex-shrink: 0;
    }
  }
`;

const ModalButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'outline' }>`
  width: 100%;
  padding: 0.85rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  ${({ variant }) => variant === 'primary' && css`
    background: #1F467E;
    color: white;
    box-shadow: 0 4px 6px -1px rgba(31, 70, 126, 0.2);
    &:hover { background: #162f56; transform: translateY(-2px); box-shadow: 0 10px 15px -3px rgba(31, 70, 126, 0.3); }
  `}

  ${({ variant }) => variant === 'secondary' && css`
    background: #10b981;
    color: white;
    box-shadow: 0 4px 6px -1px rgba(16, 185, 129, 0.2);
    &:hover { background: #059669; transform: translateY(-2px); box-shadow: 0 10px 15px -3px rgba(16, 185, 129, 0.3); }
  `}

  ${({ variant }) => variant === 'outline' && css`
    background: transparent;
    color: #1F467E;
    border-color: #1F467E;
    &:hover { background: #eff6ff; transform: translateY(-2px); }
  `}
`;

const TrustSection = styled.div`
  display: flex;
  gap: 4rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2rem;
  padding: 2rem;
  width: 100%;
  color: #64748b;
  animation: ${fadeInUp} 0.8s ease-out 0.5s backwards;
`;

const TrustItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;

  svg {
    font-size: 2rem;
    color: #94a3b8;
    margin-bottom: 0.5rem;
    transition: color 0.2s;
  }

  &:hover svg {
    color: #1F467E;
  }

  strong { font-size: 0.95rem; color: #334155; display: block; margin-bottom: 2px; }
  span { font-size: 0.85rem; }
`;

const Planos: React.FC = () => {
  const history = useHistory();
  const token = useAppSelector((state) => state.token);
  const dataExp = parseDataBRParaDate(expiraEm(token));
  const planoExpirado = dataExp ? dataExp < new Date() : false;
  const temPlanoAtivo = !ehPlanoGratuito(token) && !estaEmPeriodoDeTeste(token) && !planoExpirado;
  const [tipoPlanoAtual, setTipoPlanoAtual] = React.useState<'PRO' | 'AVANCADO' | null>(null);

  const handleBack = () => {
    history.push('/home');
  };

  React.useEffect(() => {
    const carregarPlano = async () => {
      if (!token) return;

      try {
        const plano = await obterTipoPlano(token);
        setTipoPlanoAtual(plano?.toUpperCase() as 'PRO' | 'AVANCADO');
      } catch {
        setTipoPlanoAtual(null);
      }
    };

    carregarPlano();
  }, [token]);

  React.useEffect(() => {
    iniciarSessaoFunil();
    rastrearEventoFunil('visualizou_planos', { temPlanoAtivo, tipoPlanoAtual });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showProConfirmModal, setShowProConfirmModal] = React.useState(false);
  const [showVideoModal, setShowVideoModal] = React.useState(false);

  const handleSelectPlan = (tipoClicado: 'PRO' | 'AVANCADO') => {
    rastrearEventoFunil('selecionou_plano', { plano: tipoClicado, temPlanoAtivo, tipoPlanoAtual });
    if (!temPlanoAtivo) {
      if (tipoClicado === 'PRO') {
        setShowProConfirmModal(true);
        return;
      }
      history.push(`/checkout?tipo=${tipoClicado}`);
      return;
    }

    if (tipoClicado.toUpperCase() === tipoPlanoAtual?.toUpperCase()) {
      history.push('/minha-conta?aba=assinatura');
    } else if (tipoClicado === 'AVANCADO' && tipoPlanoAtual?.toUpperCase() === 'PRO') {
      // Upgrade
      history.push(`/checkout?acao=upgrade&tipo=AVANCADO`);
    } else {
      // Downgrade
      toast.info('Para fazer downgrade de plano, entre em contato com nosso suporte.');
    }
  };

  const confirmProPlan = () => {
    rastrearEventoFunil('confirmou_plano_pro_modal', {});
    setShowProConfirmModal(false);
    history.push('/checkout?tipo=PRO');
  };

  const handleExpandir = () => {
      rastrearEventoFunil('clicou_expandir', { tipoPlanoAtual });
      history.push(`/checkout?acao=expandir&tipo=${tipoPlanoAtual}`);
  };

  // CTAs reutilizados (topo + rodapé do card) — definidos uma vez para não dessincronizar
  const ctaPro = () => (
    <ActionButton
      variant={temPlanoAtivo && tipoPlanoAtual === 'PRO' ? 'outline' : 'primary'}
      onClick={() => handleSelectPlan('PRO')}>
      {temPlanoAtivo && tipoPlanoAtual === 'PRO' ? 'Gerenciar Plano' : 'Simular e Contratar'}
    </ActionButton>
  );

  const ctaAvancado = () => (
    <ActionButton
      variant={temPlanoAtivo && (tipoPlanoAtual === 'AVANCADO') ? 'outline' : 'secondary'}
      onClick={() => handleSelectPlan('AVANCADO')}>
      {temPlanoAtivo && (tipoPlanoAtual === 'AVANCADO') ? 'Gerenciar Plano' :
       temPlanoAtivo && tipoPlanoAtual === 'PRO' ? 'Fazer Upgrade (Recomendado)' :
       'Simular e Contratar'}
    </ActionButton>
  );

  const ctaExpandir = () => (
    <ActionButton variant="primary" onClick={handleExpandir}>
      <FaCalculator /> Simular Expansão
    </ActionButton>
  );

  return (
    <Page>
      <HeroSection>
        <HeroContent>
          <TopBar>
            <BackButton onClick={handleBack}>
              <FaArrowLeft /> Voltar
            </BackButton>
          </TopBar>

          <HeaderSection>
            <Title>
              {temPlanoAtivo ? 'Evolua sua Gestão' : 'Escolha o Plano Ideal para a sua Locadora'}
            </Title>
            <Subtitle>
              {temPlanoAtivo
                ? 'Faça um upgrade para recursos avançados ou expanda sua frota.'
                : 'Comece hoje mesmo a transformar sua locadora com o LocaSmartPro.'}
            </Subtitle>
          </HeaderSection>
        </HeroContent>
      </HeroSection>

      <ContentWrapper>
        <Grid>
          {/* PLANO PRO (Mobile Order 2 - Aparece depois do Avançado no celular) */}
          <Card
            isCurrent={temPlanoAtivo && tipoPlanoAtual === 'PRO'}
            mobileOrder={2}
          >
              {temPlanoAtivo && tipoPlanoAtual === 'PRO' && <Badge color="green">Seu Plano Atual</Badge>}
            <CardBody>
              <PlanHeader>
                <PlanIcon><FaGem /></PlanIcon>
                <PlanTitle>Plano PRO</PlanTitle>
                <PlanPrice>
                  <span className="prefix" style={{ marginBottom: '-2px' }}>A partir de</span>
                  <div className="price-row"><span>R$</span><strong>79,90</strong><small>/mês</small></div>
                  <p className="description">Organize sua locadora, <strong>elimine planilhas</strong> e tenha controle financeiro real.</p>
                </PlanPrice>
              </PlanHeader>
              <div style={{ marginTop: '1.25rem' }}>{ctaPro()}</div>
              <Divider />
              <FeaturesList>
                <FeatureItem><FaCheck /> Controle financeiro completo</FeatureItem>
                <FeatureItem><FaCheck /> Contratos Automáticos ilimitados</FeatureItem>
                <FeatureItem><FaCheck /> Gestão de Manutenção e Estoque</FeatureItem>
                <FeatureItem><FaCheck /> Calendário de Recebimentos</FeatureItem>
                <MissingFeature><FaTimes /> App do Locatário</MissingFeature>
                <MissingFeature><FaTimes /> Cobrança Automática</MissingFeature>
                <MissingFeature><FaTimes /> Suporte Dedicado na Implantação</MissingFeature>
                <MissingFeature><FaTimes /> Gestão de Risco</MissingFeature>
                <MissingFeature><FaTimes /> Integrações com outros Sistemas</MissingFeature>
              </FeaturesList>
            </CardBody>
          </Card>

          {/* PLANO AVANÇADO (Mobile Order 1 - Aparece PRIMEIRO no celular) */}
          <Card
            highlight
            isCurrent={temPlanoAtivo && (tipoPlanoAtual === 'AVANCADO')}
            mobileOrder={1}
          >
              {temPlanoAtivo && (tipoPlanoAtual === 'AVANCADO')
                ? <Badge color="green">Seu Plano Atual</Badge>
                : <Badge color="blue">Mais Popular</Badge>
              }

              <CardBody>
              <PlanHeader>
                <PlanIcon style={{ color: temPlanoAtivo && (tipoPlanoAtual === 'AVANCADO') ? 'white' : '#2563eb', background: temPlanoAtivo && (tipoPlanoAtual === 'AVANCADO') ? '#10b981' : undefined }}>
                  <FaRocket />
                </PlanIcon>
                <PlanTitle>Plano AVANÇADO</PlanTitle>
                <PlanPrice>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '-2px' }}>
                    <span className="prefix">A partir de</span>
                    <span style={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: '0.9rem', fontWeight: 500 }}>R$ 139,90</span>
                  </div>

                  <div className="price-row"><span>R$</span><strong>109,90</strong><small>/mês</small></div>
                  <p className="description"><strong>Automação total:</strong> cobrança, vistoria e App do Locatário. Você gerencia menos e lucra mais.</p>
                </PlanPrice>
              </PlanHeader>
              <div style={{ marginTop: '1.25rem' }}>{ctaAvancado()}</div>
              <Divider />
              <FeaturesRow>
                <FeaturesList>
                  <FeatureItem strong><FaCheck /> Tudo do Plano PRO +</FeatureItem>
                  <FeatureItem strong><FaCheck /> Gestão de Multas completa</FeatureItem>
                  <FeatureItem strong><FaCheck /> Módulo de Vistorias</FeatureItem>
                  <FeatureItem strong><FaCheck /> Cobrança e Baixa Automática (Asaas)</FeatureItem>
                  <FeatureItem strong><FaCheck /> Assinatura Digital (sem Cartório)</FeatureItem>
                  <FeatureItem strong><FaCheck /> API para Integrações</FeatureItem>
                </FeaturesList>
                <AppHighlight>
                  <AppHighlightHeader>
                    <FaMobileAlt />
                    <span>App do Locatário</span>
                  </AppHighlightHeader>
                  <AppFeatureList>
                    <li><FaCheck /> Notificação de cobrança</li>
                    <li><FaCheck /> PIX copia e cola</li>
                    <li><FaCheck /> Documentos e contratos</li>
                    <li><FaCheck /> Checklist de vistoria</li>
                    <li><FaCheck /> Troca de óleo</li>
                  </AppFeatureList>
                  <VideoLink onClick={() => setShowVideoModal(true)}>
                    Veja como funciona &rarr;
                  </VideoLink>
                </AppHighlight>
              </FeaturesRow>
              <SupportBadge>
                <FaHeadset />
                <div>
                  <strong>Suporte Dedicado na Implantação</strong>
                  <span>Treinamento Individual ao Vivo</span>
                </div>
              </SupportBadge>
            </CardBody>
          </Card>

          {/* CARD EXPANDIR LIMITE (Mobile Order 3 - Aparece por último) */}
          {temPlanoAtivo && (
              <Card mobileOrder={3}>
                  <Badge color="yellow">Expansão</Badge>
                  <CardBody>
                      <PlanHeader>
                        <PlanIcon><FaCar /></PlanIcon>
                        <PlanTitle>Expandir Frota</PlanTitle>
                        <div style={{ padding: '1rem 0', color: '#64748b' }}>
                           <p>Sua locadora cresceu? Aumente seu limite de veículos conforme sua necessidade.</p>
                        </div>
                      </PlanHeader>
                      <div style={{ marginTop: '1.25rem' }}>{ctaExpandir()}</div>
                      <Divider />
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '1rem' }}>
                         <FaCalculator style={{ fontSize: '3rem', color: '#cbd5e1' }} />
                         <p style={{ color: '#475569', fontSize: '0.95rem' }}>
                            Simule o valor para adicionar mais veículos diretamente no checkout. O cálculo é feito proporcionalmente ao seu ciclo atual.
                         </p>
                      </div>
                  </CardBody>
              </Card>
          )}
        </Grid>

        {/* ÁREA DE CONFIANÇA */}
        <TrustSection>
          <TrustItem><FaShieldAlt /><div><strong>Segurança Total</strong><span>Criptografia de ponta</span></div></TrustItem>
          <TrustItem><FaServer /><div><strong>Backup Automático</strong><span>Seus dados sempre salvos</span></div></TrustItem>
          <TrustItem><FaHeadset /><div><strong>Suporte Premium</strong><span>Atendimento humanizado</span></div></TrustItem>
        </TrustSection>

      </ContentWrapper>

      {showVideoModal && (
        <ModalOverlay onClick={() => setShowVideoModal(false)}>
          <VideoModalContent onClick={(e) => e.stopPropagation()}>
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/1d-CPjszMfQ?autoplay=1"
              title="App do Locatário"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </VideoModalContent>
        </ModalOverlay>
      )}

      {showProConfirmModal && (
        <ModalOverlay onClick={() => setShowProConfirmModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Tem certeza que deseja o Plano PRO?</ModalTitle>
            <ModalDescription>
              No plano <strong>Avançado</strong> você teria acesso a:
            </ModalDescription>
            <ModalFeatureList>
              <li><FaCheck /> App Exclusivo do Locatário com PIX copia e cola</li>
              <li><FaCheck /> Notificação de cobrança automática para o locatário</li>
              <li><FaCheck /> Gestão de Multas completa</li>
              <li><FaCheck /> Módulo de Vistorias</li>
              <li><FaCheck /> Cobrança e Baixa Automática (Asaas)</li>
              <li><FaCheck /> Assinatura Digital sem Cartório</li>
              <li><FaHeadset /> Suporte Dedicado + Treinamento Individual ao Vivo</li>
            </ModalFeatureList>
            <ModalButtons>
              <ActionButton variant="secondary" onClick={() => { setShowProConfirmModal(false); history.push('/checkout?tipo=AVANCADO'); }}>
                Quero o Avançado
              </ActionButton>
              <ActionButton variant="outline" onClick={confirmProPlan}>
                Continuar com PRO
              </ActionButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </Page>
  );
};

export default Planos;
